import { useEffect, useState } from 'react';

import { DisplayComponent } from '@/components/DisplayComponent';
import { GenericLenderCard } from '@/components/GenericLenderCard';
import { Header } from '@/components/Header';
import { ProgressBar } from '@/components/ProgressBar';
import { Slogan } from '@/components/Slogan';
import { TruckstopQuickCalculatorIcons } from '@/components/TruckstopQuickCalculatorIcons';
import { EVehicleAssetType, selectVehicleAssetType } from '@/context/borrower';
import { selectPartner, selectPartnerName } from '@/context/display';
import { store } from '@/context/store';
import { useAppSelector } from '@/context/storeHooks';
import { TRUCKSTOP_PARTNER_NAME } from '@/partners/truckstop';
import { AssetTypeSelector } from '@/UserInput/AssetType';
import { LoanTerm } from '@/UserInput/LoanTerm';
import { LoanUsage } from '@/UserInput/LoanUsage';
import { PurchasePrice } from '@/UserInput/PurchasePrice';
import { SelfAssessedCredit } from '@/UserInput/SelfAssessedCredit';
import { StorePersonName } from '@/UserInput/StorePersonName';
import {
    ELocalStorageVariables,
    getLocalStorageItem,
} from '@/utils/localStorage';

import { QuickCalculatorButton } from './QuickCalculatorButton';

export const testId = 'QuickCalculator';

export function QuickCalculator(): JSX.Element {
    // ***** LOCAL STATE *****
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [hasData, setHasData] = useState<boolean>(false);

    // ***** CONSTANTS *****
    const baseId = 'screen-quickCalculator';
    const partnerName = useAppSelector(selectPartnerName);
    const partner = useAppSelector(selectPartner);
    const storePersonName = partner?.storePersonName;
    const isTruckstop = partnerName === TRUCKSTOP_PARTNER_NAME;
    const pathname = window.location.pathname;
    const isAssetTypePersonalLoan =
        useAppSelector(selectVehicleAssetType) ===
        EVehicleAssetType.PERSONAL_LOAN;
    const title = isAssetTypePersonalLoan
        ? 'how much are you looking to borrow?'
        : "What's the purchase price?";

    const isJayco = pathname.includes('jaycoloading');
    const authToken = getLocalStorageItem(ELocalStorageVariables.AUTH_TOKEN);
    const firstName = getLocalStorageItem(ELocalStorageVariables.FIRST_NAME);
    const state = store.getState();
    const maritalStatus = state.borrowerReducer.personalDetails.maritalStatus;
    useEffect(() => {
        if (authToken && firstName && maritalStatus) {
            setHasData(true);
        }
    }, [authToken, firstName, maritalStatus]);

    if (isJayco) {
        return (
            <>
                <div className='flex flex-col justify-center items-center w-full'>
                    <div className='h-60 w-60 pt-40'>
                        <img src='https://driveiq-assets.s3.ap-southeast-2.amazonaws.com/vroom/jayco.svg' />
                    </div>
                    <div>
                        <h1 className='text-xl pt-16 font-semibold'>
                            FINANCIAL SERVICES
                        </h1>
                    </div>
                    <div className='flex space-x-2 pt-6'>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-1'></div>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-2'></div>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-3'></div>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-4'></div>
                        <div className='dot bg-blue-500 w-4 h-4 rounded-full animate-pulse-dots-5'></div>
                    </div>
                </div>
            </>
        );
    }
    const isBrandNovated = state.displayReducer.partner.isNovatedLease;

    function RenderAssetTypeSelector(): JSX.Element {
        if (!isAssetTypePersonalLoan) {
            if (isBrandNovated) {
                return <LoanUsage baseId={baseId} showErrors={false} />;
            }
            if (partner?.isScoreToAsset) {
                return <AssetTypeSelector showErrors={false} baseId={baseId} />;
            }
        }
        return <SelfAssessedCredit showErrors={false} baseId={baseId} />;
    }

    return (
        <DisplayComponent baseId={baseId} data-testid={testId}>
            <Header />
            <Slogan />
            <ProgressBar />
            <PurchasePrice
                showErrors={showErrors}
                baseId={baseId}
                title={title}
            />
            <LoanTerm baseId={baseId} />
            {storePersonName && <StorePersonName baseId={baseId} />}
            <RenderAssetTypeSelector />
            <GenericLenderCard />
            <div className='h-4 w-full' />
            <QuickCalculatorButton setShowErrors={setShowErrors} />
            {isTruckstop && <TruckstopQuickCalculatorIcons />}
            {/* Empty div below is in place of traditional back/next buttons as display component has extra styling in place for the last element */}
            <div />
        </DisplayComponent>
    );
}
